/**
 * Resolving which production methods apply to a CN good.
 *
 * The emissions modal used to do this inline in two places, each doing it
 * slightly differently, and each *only* assigning on success — so a good whose
 * category could not be resolved silently kept the previously opened good's
 * methods. That is worse than showing nothing: it offers methods belonging to
 * another category with no indication, and saving writes them.
 *
 * Resolution order:
 *
 * 1. The CN good object itself, when it carries `aggregatedGoodsCategory`.
 *    Shipment lines embed the whole CnGood document, so this is usually a
 *    direct hit and skips the catalog entirely.
 * 2. Exact match against the catalog from `GET /cngoods/cbam`.
 * 3. Prefix match against the catalog. That endpoint returns only fully
 *    qualified 8-digit codes (`$strLenCP === 10`, backend cngoods.ts:85), but
 *    a large share of default emission factors are keyed on heading-level
 *    codes such as "3102 30". Those can never match exactly, which left the
 *    Production Methods tab empty with no way to satisfy its own "at least one
 *    production method is required" validation.
 *
 * Category names are trimmed before lookup: the catalog contains "Electricity "
 * with a trailing space while ProdMethods.json has "Electricity".
 */

/** Codes are stored spaced ("3102 30 90"); compare without spacing. */
const normalizeCode = (value) => String(value ?? '').replace(/\s+/g, '');

/**
 * @param {object|string|null|undefined} cnGood - Populated CN good or its code.
 * @param {Array} cnGoods - Catalog from `useCnGoods()`.
 * @returns {string} The aggregated goods category, or '' when unresolvable.
 */
export const resolveGoodsCategory = (cnGood, cnGoods) => {
  if (cnGood && typeof cnGood === 'object' && cnGood.aggregatedGoodsCategory) {
    return String(cnGood.aggregatedGoodsCategory).trim();
  }

  const code = typeof cnGood === 'object' ? (cnGood?.cnCode ?? cnGood?._id) : cnGood;
  const normalized = normalizeCode(code);
  if (!normalized || !cnGoods?.length) return '';

  const exact = cnGoods.find(
    (good) => normalizeCode(good?.cnCode) === normalized || normalizeCode(good?._id) === normalized,
  );
  if (exact?.aggregatedGoodsCategory) return String(exact.aggregatedGoodsCategory).trim();

  // Heading-level code: every fully qualified good beneath it shares a
  // category in practice, so the first match is representative.
  const byPrefix = cnGoods.find((good) => normalizeCode(good?.cnCode).startsWith(normalized));
  return byPrefix?.aggregatedGoodsCategory ? String(byPrefix.aggregatedGoodsCategory).trim() : '';
};

/**
 * @param {object|string|null|undefined} cnGood - Populated CN good or its code.
 * @param {Array} cnGoods - Catalog from `useCnGoods()`.
 * @param {Record<string, Array>} productionMethods - ProdMethods.json.
 * @returns {Array} Methods for the good's category, sorted; [] when unresolvable.
 */
export const resolveProductionMethods = (cnGood, cnGoods, productionMethods) => {
  const category = resolveGoodsCategory(cnGood, cnGoods);
  if (!category) return [];
  const methods = productionMethods?.[category];
  if (!Array.isArray(methods) || methods.length === 0) return [];
  return [...methods].sort((a, b) => (b > a ? 1 : -1));
};
